import { useCallback, useEffect, useRef, useState, type PointerEvent, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EstadoError, ListaEsqueleto } from '../../components/ui/Estados';
import { Icon } from '../../components/ui/Icon';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import { repositorioAlarmas } from '../../../data/repositories/RepositorioAlarmas';
import type { Alarma } from '../../../domain/entities/Alarma';
import type { ColorMarcador } from '../../../domain/entities/PanelPersonal';
import { useTiempoReal } from '../../hooks/useTiempoReal';
import { formatearAntiguedad } from '../../utils/formato';
import { SelectorOrigen, type Elegible } from './SelectorOrigen';
import { SelectorPanel } from './SelectorPanel';
import { usePanel } from './usePanel';
import { FondoCamara } from './FondoCamara';
import { useValoresPanel } from './useValoresPanel';
import { repositorioCctv } from '../../../data/repositories/RepositorioCctv';
import estilos from './Panel.module.css';

/**
 * Paleta de los marcadores, idéntica a la de la versión anterior.
 * Un marcador cuyo sensor está en alarma se pinta rojo sea cual sea su color.
 */
const COLOR_MARCADOR: Record<ColorMarcador, { hex: string; fondo: string }> = {
  verde: { hex: '#34D399', fondo: 'rgba(52,211,153,0.3)' },
  naranja: { hex: '#F5A623', fondo: 'rgba(245,166,35,0.3)' },
  rojo: { hex: '#EF4444', fondo: 'rgba(239,68,68,0.3)' },
  azul: { hex: '#3B82F6', fondo: 'rgba(59,130,246,0.3)' },
};

/** Cómo se pinta cada criticidad en la actividad reciente. */
const CRITICIDAD_FEED: Record<
  string,
  { icono: 'advertencia' | 'info' | 'circulo'; fondo: string; color: string; clase: string }
> = {
  critica: {
    icono: 'advertencia',
    fondo: 'rgba(239,68,68,0.2)',
    color: '#EF4444',
    clase: 'critCritica',
  },
  alta: {
    icono: 'advertencia',
    fondo: 'rgba(239,68,68,0.15)',
    color: '#EF4444',
    clase: 'critAlta',
  },
  media: {
    icono: 'advertencia',
    fondo: 'rgba(245,158,11,0.2)',
    color: '#F59E0B',
    clase: 'critMedia',
  },
  baja: { icono: 'info', fondo: 'rgba(59,130,246,0.2)', color: '#3B82F6', clase: 'critBaja' },
};

const CRITICIDAD_FEED_DEFECTO = {
  icono: 'circulo' as const,
  fondo: 'rgba(148,163,184,0.2)',
  color: '#64748B',
  clase: '',
};

/** Qué elemento está eligiendo sensor. */
type Objetivo = { tipo: 'marcador' | 'tarjeta'; id: string } | null;

/** Valor en vivo de un sensor, con su estado, tal como lo pinta el panel. */
export function Panel() {
  const p = usePanel();
  const clienteConsultas = useQueryClient();

  const [objetivo, setObjetivo] = useState<Objetivo>(null);
  const [globoAbierto, setGloboAbierto] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState<string | null>(null);
  const [catalogoAbierto, setCatalogoAbierto] = useState(false);
  const [feedPlegado, setFeedPlegado] = useState(false);
  const [sello, setSello] = useState<'oculto' | 'guardado' | 'error'>('oculto');

  const refPlano = useRef<HTMLDivElement>(null);
  const refArchivo = useRef<HTMLInputElement>(null);
  const refSelloTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Reloj del topbar, como el original: HH:MM refrescado cada 30 s. ──
  const [hora, setHora] = useState(() => new Date());
  useEffect(() => {
    const temporizador = window.setInterval(() => setHora(new Date()), 30_000);
    return () => window.clearInterval(temporizador);
  }, []);

  const reloj = `${String(hora.getHours()).padStart(2, '0')}:${String(hora.getMinutes()).padStart(2, '0')}`;

  // ── Actividad reciente: las alarmas abiertas más nuevas, en vivo. ──
  const alarmas = useQuery({
    queryKey: ['panel', 'alarmas-feed'],
    queryFn: () => repositorioAlarmas.listar({ estado: 'abiertas', pagina: 1, porPagina: 8 }),
  });

  useTiempoReal({ tabla: 'alarmas', evento: '*' }, () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['panel', 'alarmas-feed'] });
  });

  const feed: readonly Alarma[] = alarmas.data?.filas ?? [];

  // ── Salir de edición guarda y muestra el sello «Guardado ✓». ──
  const terminarEdicion = useCallback(async () => {
    p.alternarEdicion();
    setCatalogoAbierto(false);

    const ok = await p.guardarAhora();
    setSello(ok ? 'guardado' : 'error');

    if (refSelloTimer.current) clearTimeout(refSelloTimer.current);
    refSelloTimer.current = setTimeout(() => setSello('oculto'), ok ? 2000 : 4000);
  }, [p]);

  useEffect(() => {
    return () => {
      if (refSelloTimer.current) clearTimeout(refSelloTimer.current);
    };
  }, []);

  // ── Arrastre de marcadores, acotado al 2–98 % como el original. ──
  const posicionRelativa = useCallback((e: PointerEvent) => {
    const caja = refPlano.current?.getBoundingClientRect();
    if (!caja || caja.width === 0 || caja.height === 0) return null;

    return {
      x: Math.min(98, Math.max(2, ((e.clientX - caja.left) / caja.width) * 100)),
      y: Math.min(98, Math.max(2, ((e.clientY - caja.top) / caja.height) * 100)),
    };
  }, []);

  const alMoverMarcador = useCallback(
    (e: PointerEvent) => {
      if (!arrastrando) return;
      const posicion = posicionRelativa(e);
      if (posicion) p.cambiar((actual) => actual.actualizarMarcador(arrastrando, posicion));
    },
    [arrastrando, posicionRelativa, p],
  );

  const elegirOrigen = useCallback(
    (elegido: Elegible) => {
      if (!objetivo) return;

      /*
       * Se guardan el origen y la referencia, y además el nombre y el área.
       * Duplicar esos textos es a propósito: así el panel sigue leyéndose
       * aunque el elemento se borre o deje de estar al alcance de quien mira,
       * en vez de quedar una tarjeta muda con un identificador dentro.
       */
      if (objetivo.tipo === 'marcador') {
        p.cambiar((actual) =>
          actual.actualizarMarcador(objetivo.id, {
            origen: elegido.origen,
            referencia: elegido.referencia,
            sensorId: elegido.origen === 'sensor' ? Number(elegido.referencia) : null,
            sensorNombre: elegido.nombre,
            sensorTipo: elegido.tipo,
            sensorArea: elegido.detalle,
            sensorUnidad: elegido.unidad,
          }),
        );
      } else {
        p.cambiar((actual) =>
          actual.actualizarTarjeta(objetivo.id, {
            origen: elegido.origen,
            referencia: elegido.referencia,
            sensorId: elegido.origen === 'sensor' ? Number(elegido.referencia) : null,
            etiqueta: elegido.nombre,
            descripcion: elegido.detalle,
            unidad: elegido.unidad,
            tipo: elegido.tipo,
            area: elegido.detalle,
          }),
        );
      }

      setObjetivo(null);
    },
    [objetivo, p],
  );

  /*
   * TODOS los hooks van antes de cualquier `return`.
   *
   * React identifica cada hook por su orden de llamada, así que si una salida
   * temprana se cuela en medio, el renderizado siguiente llama a menos y
   * revienta con el error 310. Por eso lo que sigue tolera que el panel aún no
   * exista en vez de esperar a que exista.
   */

  /*
   * Qué hace falta pedir, según lo que el panel muestre. Un panel de puros
   * sensores no carga medidores ni actuadores.
   */
  const referencias = useMemo(() => {
    const porOrigen = { sensores: [], nodos: [], medidores: [], actuadores: [] } as {
      sensores: number[];
      nodos: number[];
      medidores: number[];
      actuadores: number[];
    };

    const elementos = p.panel ? [...p.panel.marcadores, ...p.panel.tarjetas] : [];

    for (const elemento of elementos) {
      if (!elemento.referencia) continue;
      const numero = Number(elemento.referencia);

      if (elemento.origen === 'sensor' && Number.isFinite(numero)) porOrigen.sensores.push(numero);
      if (elemento.origen === 'nodo' && Number.isFinite(numero)) porOrigen.nodos.push(numero);
      if (elemento.origen === 'actuador' && Number.isFinite(numero))
        porOrigen.actuadores.push(numero);
      if (elemento.origen === 'medidor') porOrigen.medidores.push(0);
    }

    return porOrigen;
  }, [p.panel]);

  const valores = useValoresPanel(referencias);

  /*
   * Las cámaras sólo se piden en edición: quien mira el panel no necesita la
   * lista, y en la carga normal serían una consulta más para nada.
   */
  const camaras = useQuery({
    queryKey: ['cctv', 'camaras', null],
    queryFn: () => repositorioCctv.listarCamaras(null),
    enabled: p.editando,
    staleTime: 60_000,
  });

  if (p.cargando) return <ListaEsqueleto filas={5} />;
  if (p.error || !p.panel) {
    return <EstadoError titulo="No se pudo cargar tu panel" />;
  }

  const panel = p.panel;
  const fondo = panel.fondo;

  const clasesRaiz = [
    estilos.raiz,
    fondo.tipo === 'vacio' ? estilos.sinImagen : estilos.conImagen,
    p.editando ? estilos.editando : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina titulo="Panel" descripcion="Tu vista personal de la instalación, en vivo." />

      <div className={clasesRaiz}>
        {/* Fondo: un plano fijo, una cámara en vivo, o la invitación a poner uno. */}
        <div className={estilos.fondo}>
          {fondo.tipo === 'imagen' ? (
            <img src={fondo.url} alt={panel.nombreInstalacion} />
          ) : fondo.tipo === 'camara' ? (
            <FondoCamara camaraId={fondo.camaraId} />
          ) : (
            <div className={estilos.fondoVacio}>
              <div className={estilos.fondoVacioIcono}>🏠</div>
              <div className={estilos.fondoVacioTitulo}>Sin fondo</div>
              <div className={estilos.fondoVacioTexto}>
                Pulsa <b>Editar</b> y elige una imagen del plano o una cámara en vivo
              </div>
            </div>
          )}
          <div className={estilos.velo} />
        </div>

        {/* Topbar interno. */}
        <div className={estilos.topbar}>
          <div className={estilos.topbarIzquierda}>
            <SelectorPanel
              paneles={p.paneles}
              activoId={panel.id}
              editando={p.editando}
              onAbrir={(id) => void p.abrirPanel(id)}
              onCrear={(nombre) => void p.crearPanel(nombre)}
              onBorrar={(id) => void p.borrarPanel(id)}
            />
            <div className={estilos.etiquetaVivo}>En vivo</div>
          </div>

          <div className={estilos.topbarDerecha}>
            <div
              className={[
                estilos.selloGuardado,
                sello !== 'oculto' ? estilos.selloGuardadoVisible : '',
                sello === 'error' ? estilos.selloGuardadoError : '',
              ]
                .filter(Boolean)
                .join(' ')}
              role="status"
            >
              <Icon nombre={sello === 'error' ? 'advertencia' : 'check'} tamano={14} />
              {sello === 'error' ? 'Error al guardar' : 'Guardado'}
            </div>

            <div className={estilos.reloj}>{reloj}</div>

            {!p.editando && (
              <button
                type="button"
                className={`${estilos.boton} ${estilos.botonContorno}`}
                title="Editar vista"
                onClick={p.alternarEdicion}
              >
                <Icon nombre="editar" tamano={16} />
                <span>Editar</span>
              </button>
            )}

            {p.editando && (
              <button
                type="button"
                className={`${estilos.boton} ${estilos.botonPrimario}`}
                title="Agregar widget"
                onClick={() => setCatalogoAbierto((v) => !v)}
              >
                <Icon nombre="agregar" tamano={16} />
                <span>Widget</span>
              </button>
            )}
          </div>
        </div>

        {/* Banner de modo edición. */}
        {p.editando && (
          <div className={estilos.bannerEdicion}>
            <span className={estilos.puntoEdicion} />
            <span>Modo edición activo</span>
            {/*
              La pista, en un texto corto y sin iconos intercalados. Con el
              engranaje y la equis dentro, el navegador la trataba como tres
              trozos y los repartía en tres renglones aunque sobrara ancho.
            */}
            <span className={estilos.pistaEdicion}>
              Arrastra los pines · el engranaje elige qué muestra · la equis lo borra
            </span>
            <div className={estilos.separadorBanner} />
            <button
              type="button"
              className={`${estilos.boton} ${estilos.botonFantasma}`}
              onClick={() => refArchivo.current?.click()}
            >
              <Icon nombre="imagen" tamano={16} /> Cambiar imagen
            </button>

            {/*
              O una cámara en vivo en vez del plano. Va aquí, al lado del botón
              de la imagen, porque es la misma decisión: qué se ve de fondo.
            */}
            <select
              className={estilos.selectorFondo}
              value={fondo.tipo === 'camara' ? fondo.camaraId : ''}
              onChange={(e) => p.elegirCamara(e.target.value ? Number(e.target.value) : null)}
              aria-label="Cámara de fondo"
              title="Poner una cámara en vivo de fondo"
            >
              <option value="">Fondo: imagen</option>
              {(camaras.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  Cámara: {c.nombre}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`${estilos.boton} ${estilos.botonFantasma}`}
              onClick={() => {
                const nombre = window.prompt('Nombre de la instalación:', panel.nombreInstalacion);
                if (nombre?.trim()) p.cambiar((a) => a.conNombre(nombre.trim()));
              }}
            >
              <Icon nombre="editar" tamano={16} /> Renombrar
            </button>
            <button
              type="button"
              className={`${estilos.boton} ${estilos.botonExito}`}
              onClick={() => void terminarEdicion()}
            >
              <Icon nombre="check" tamano={16} /> Listo
            </button>
          </div>
        )}

        {/* Capa de marcadores sobre la imagen. */}
        <div
          ref={refPlano}
          className={estilos.capaMarcadores}
          onPointerMove={alMoverMarcador}
          onPointerUp={() => setArrastrando(null)}
          onPointerLeave={() => setArrastrando(null)}
        >
          {panel.marcadores.map((m) => {
            const vivo = valores.valorDe(m.origen, m.referencia);
            const color = COLOR_MARCADOR[vivo.enAlarma ? 'rojo' : m.color];

            /*
             * Se mira la referencia y no el identificador de sensor: ese campo sólo se rellena
             * con sensores, así que un marcador puesto sobre un nodo, un
             * medidor o un actuador se veía como «sin asignar» estándolo.
             */
            const asignado = Boolean(m.referencia);
            const tituloGlobo = asignado ? m.sensorNombre : 'Sin asignar';
            const textoGlobo = asignado
              ? [vivo.texto, m.sensorArea].filter(Boolean).join(' · ')
              : 'Pulsa el engranaje para elegir qué muestra';

            return (
              <div
                key={m.id}
                className={[
                  estilos.marcador,
                  arrastrando === m.id ? estilos.marcadorArrastrando : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ left: `${m.x}%`, top: `${m.y}%` }}
                role="button"
                tabIndex={0}
                aria-label={tituloGlobo}
                onPointerDown={(e) => {
                  if (!p.editando) return;
                  const objetivoPulsado = e.target as HTMLElement;
                  if (objetivoPulsado.closest('button')) return;
                  e.preventDefault();
                  setArrastrando(m.id);
                }}
                onClick={(e) => {
                  if (p.editando) return;
                  if ((e.target as HTMLElement).closest('button')) return;
                  setGloboAbierto((abierto) => (abierto === m.id ? null : m.id));
                }}
              >
                <div className={estilos.marcadorPulso} style={{ background: color.hex }} />
                <div
                  className={estilos.marcadorPunto}
                  style={{ background: color.fondo, color: color.hex }}
                >
                  ●
                </div>

                {globoAbierto === m.id && !p.editando && (
                  <div className={estilos.globo}>
                    <div className={estilos.globoTitulo}>{tituloGlobo}</div>
                    <div className={estilos.globoTexto}>{textoGlobo}</div>
                  </div>
                )}

                <button
                  type="button"
                  className={estilos.insigniaElegir}
                  title="Elegir sensor"
                  onClick={(e) => {
                    e.stopPropagation();
                    setObjetivo({ tipo: 'marcador', id: m.id });
                  }}
                >
                  <Icon nombre="ajustes" tamano={11} />
                </button>
                <button
                  type="button"
                  className={estilos.insigniaQuitar}
                  title="Eliminar"
                  onClick={(e) => {
                    e.stopPropagation();
                    p.cambiar((a) => a.quitarMarcador(m.id));
                  }}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>

        <input
          ref={refArchivo}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const archivo = e.target.files?.[0];
            if (archivo) void p.subirImagen(archivo);
            e.target.value = '';
          }}
        />

        {/* Panel inferior: tarjetas de valor + actividad reciente. */}
        <div className={estilos.inferior}>
          <div className={estilos.tarjetas}>
            {panel.tarjetas.map((t) => {
              const vivo = valores.valorDe(t.origen, t.referencia);

              const colorValor = !vivo.conDatos
                ? 'var(--dash-tx)'
                : vivo.enAlarma
                  ? '#EF4444'
                  : '#34D399';

              const texto =
                [t.tipo, t.area].filter(Boolean).join(' · ') ||
                t.descripcion ||
                'Clic en ⚙ para elegir sensor';

              return (
                <div
                  key={t.id}
                  className={[estilos.tarjeta, vivo.enAlarma ? estilos.tarjetaAlerta : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className={estilos.tarjetaEtiqueta}>{t.etiqueta}</div>
                  <div className={estilos.tarjetaValor} style={{ color: colorValor }}>
                    {vivo.conDatos ? vivo.texto : '—'}
                  </div>
                  <div className={estilos.tarjetaTexto}>{texto}</div>

                  <button
                    type="button"
                    className={estilos.insigniaElegir}
                    title="Elegir sensor"
                    onClick={() => setObjetivo({ tipo: 'tarjeta', id: t.id })}
                  >
                    <Icon nombre="ajustes" tamano={11} />
                  </button>
                  <button
                    type="button"
                    className={estilos.insigniaQuitar}
                    title="Eliminar"
                    onClick={() => p.cambiar((a) => a.quitarTarjeta(t.id))}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          <div
            className={[estilos.feed, feedPlegado ? estilos.feedPlegado : '']
              .filter(Boolean)
              .join(' ')}
          >
            <button
              type="button"
              className={estilos.feedCabecera}
              onClick={() => setFeedPlegado((v) => !v)}
              aria-expanded={!feedPlegado}
            >
              <span className={estilos.feedTitulo}>Actividad reciente</span>
              <span className={estilos.feedConmutador}>
                <span className={estilos.feedContador}>{feed.length}</span>
                <svg className={estilos.feedFlecha} viewBox="0 0 24 24" aria-hidden>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>

            <div className={estilos.feedCuerpo}>
              {alarmas.isLoading ? (
                <div className={estilos.feedVacio}>Cargando alarmas...</div>
              ) : feed.length === 0 ? (
                <div className={estilos.feedVacio}>No hay alarmas activas</div>
              ) : (
                feed.map((a) => {
                  const criticidad = CRITICIDAD_FEED[a.criticidad.valor] ?? CRITICIDAD_FEED_DEFECTO;
                  const claseCrit = criticidad.clase
                    ? estilos[criticidad.clase as keyof typeof estilos]
                    : undefined;

                  return (
                    <div key={a.id} className={estilos.feedElemento}>
                      <div
                        className={estilos.feedIcono}
                        style={{ background: criticidad.fondo, color: criticidad.color }}
                      >
                        <Icon nombre={criticidad.icono} tamano={18} />
                      </div>
                      <div className={estilos.feedPrincipal}>
                        <div className={estilos.feedMensaje}>{a.sensorNombre}</div>
                        <div className={estilos.feedMeta}>
                          <span className={estilos.feedArea}>
                            <Icon nombre="mapa" tamano={11} /> {a.areaNombre ?? '—'}
                          </span>
                          <span className={`${estilos.feedCriticidad} ${claseCrit ?? ''}`}>
                            {a.criticidad.etiqueta}
                          </span>
                        </div>
                        <div className={estilos.feedHora}>{formatearAntiguedad(a.fechaInicio)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Catálogo de widgets. */}
        {catalogoAbierto && p.editando && (
          <div className={estilos.catalogo}>
            <div className={estilos.catalogoCabecera}>
              <div className={estilos.catalogoTitulo}>Añadir un pin</div>
              <button
                type="button"
                className={estilos.catalogoCerrar}
                onClick={() => setCatalogoAbierto(false)}
              >
                ✕
              </button>
            </div>
            <p className={estilos.catalogoNota}>
              El color es sólo para organizarte: en alarma, cualquier pin se pone rojo.
            </p>

            <div className={estilos.catalogoCuerpo}>
              {(
                [
                  /*
                    El color sólo se ve mientras todo va bien: en alarma, los tres se
                    ponen rojos. Sirve para agrupar de un vistazo —lo eléctrico en
                    naranja, lo informativo en azul—, no para decir la gravedad.
                  */
                  ['verde', 'Pin verde', 'Para lo que se vigila a diario'],
                  ['naranja', 'Pin naranja', 'Para destacar un grupo'],
                  ['azul', 'Pin azul', 'Para lo puramente informativo'],
                ] as const
              ).map(([color, nombre, detalle]) => (
                <button
                  key={color}
                  type="button"
                  className={estilos.catalogoElemento}
                  onClick={() => p.cambiar((a) => a.agregarMarcador(color))}
                >
                  <div
                    className={estilos.catalogoMuestra}
                    style={{
                      background: COLOR_MARCADOR[color].fondo,
                      borderColor: COLOR_MARCADOR[color].hex,
                      color: COLOR_MARCADOR[color].hex,
                    }}
                  >
                    ●
                  </div>
                  <div className={estilos.catalogoInfo}>
                    <div className={estilos.catalogoNombre}>{nombre}</div>
                    <div className={estilos.catalogoDetalle}>{detalle}</div>
                  </div>
                </button>
              ))}

              <div className={estilos.catalogoSeparador} />

              <button
                type="button"
                className={estilos.catalogoElemento}
                onClick={() => p.cambiar((a) => a.agregarTarjeta())}
              >
                <div
                  className={estilos.catalogoMuestra}
                  style={{ background: 'var(--dash-glass-b)' }}
                >
                  <span style={{ fontSize: 10, fontWeight: 600 }}>123</span>
                </div>
                <div className={estilos.catalogoInfo}>
                  <div className={estilos.catalogoNombre}>Tarjeta de valor</div>
                  <div className={estilos.catalogoDetalle}>
                    Muestra el valor actual de un sensor
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      <SelectorOrigen
        abierto={objetivo !== null}
        onCerrar={() => setObjetivo(null)}
        onElegir={elegirOrigen}
      />
    </div>
  );
}
